import { useState, useRef, useCallback, useEffect } from 'preact/hooks';

// Web Speech API types (not always in TS lib)
type SpeechRecognitionAny = any;

// Browser compat: Chrome uses webkit prefix
const SR: (new () => SpeechRecognitionAny) | null =
  (typeof window !== 'undefined' &&
    ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)) || null;

export const speechRecognitionSupported = !!SR;

export type MicPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

export function useSpeechRecognition() {
  const [listening, setListening] = useState(false);
  const [micPermission, setMicPermission] = useState<MicPermission>('unknown');
  const recRef = useRef<SpeechRecognitionAny | null>(null);

  // Check mic permission state on mount and listen for changes
  useEffect(() => {
    if (!navigator.permissions) return;
    navigator.permissions.query({ name: 'microphone' as PermissionName }).then((status) => {
      setMicPermission(status.state as MicPermission);
      status.onchange = () => setMicPermission(status.state as MicPermission);
    }).catch(() => {
      setMicPermission('unknown');
    });
  }, []);

  /**
   * Start speech recognition.
   * Fires getUserMedia as a non-blocking side-effect so Android shows its permission
   * dialog, but starts SpeechRecognition synchronously to preserve the user gesture.
   */
  const start = useCallback((
    onResult: (text: string) => void,
    lang?: string,
    onPartial?: (text: string) => void,
    onError?: (message: string) => void,
  ) => {
    if (!SR) {
      onError?.('Speech recognition not supported in this browser');
      onResult('');
      return;
    }

    // Non-blocking: trigger Android permission dialog as a side-effect.
    // We do NOT await — SpeechRecognition.start() must happen synchronously
    // within the user gesture or mobile browsers will reject it.
    if (micPermission !== 'granted') {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((stream) => { stream.getTracks().forEach((t) => t.stop()); setMicPermission('granted'); })
        .catch(() => setMicPermission('denied'));
    }

    const rec = new SR();
    rec.lang = lang || navigator.language || 'en-US';
    rec.continuous = false;
    rec.interimResults = !!onPartial;

    rec.onresult = (e: any) => {
      let interim = '';
      let final = '';
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          final += e.results[i][0].transcript;
        } else {
          interim += e.results[i][0].transcript;
        }
      }
      if (final) {
        onResult(final.trim());
      } else if (interim && onPartial) {
        onPartial(interim.trim());
      }
    };

    rec.onerror = (e: any) => {
      setListening(false);
      if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') {
        setMicPermission('denied');
      }
      const msg = (e?.error === 'not-allowed' || e?.error === 'service-not-allowed')
        ? 'Microphone blocked — tap ⋮ → Settings → Site settings → Microphone → Allow'
        : e?.error === 'network'
          ? 'Speech recognition needs an internet connection'
          : e?.error === 'no-speech'
            ? '' // silent — expected when user doesn't speak
            : `Speech recognition failed (${e?.error ?? 'unknown'}) — try again`;
      if (msg) onError?.(msg);
      onResult('');
    };

    rec.onend = () => setListening(false);

    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (err: any) {
      setListening(false);
      setMicPermission('denied');
      onError?.(`Could not start microphone: ${err?.message ?? 'unknown error'}`);
      onResult('');
    }
  }, [micPermission]);

  const stop = useCallback(() => {
    recRef.current?.stop();
    setListening(false);
  }, []);

  return { supported: !!SR, listening, micPermission, start, stop };
}
